import {
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { environment } from '@emma-environments/environment';
import { areArraysEquivalent, haveArraysSameValues, sortByProp } from '@emma-helpers/emma-utils';
import { formatNumber } from '@emma-helpers/format.helper';
import { CsvExporterService } from '@emma-services/csv-exporter.service';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { OnUnsubscribe } from '@platform/helpers/on-unsubscribe.class';
import { UserMetaService } from '@platform/services/user-meta.service';
import { chain, debounce, get, isNil, set } from 'lodash';
import { takeUntil } from 'rxjs/operators';

import {
  EMMATableActionsLeftTemplateDirective,
  EMMATableActionsRightTemplateDirective,
  EMMATableFooterTemplateDirective,
  EMMATableHeaderTemplateDirective,
  EMMATableNoResultsTemplateDirective,
} from './emma-table.directive';
import {
  CALCULATOR_METHOD,
  COLUMN_DATA_TYPE,
  COLUMN_SORT_DIRECTION,
  GENERIC_EXPORT_FILENAME,
  INPUT_FILTER_DEBOUNCE_DELAY,
  NUMBER_TYPES,
  SUMMARY_POSITION,
  filterRowFactory,
  getColumnSummary,
  getColumnValue,
} from './emma-table.helpers';
import {
  EMMATableAction,
  EMMATableColumn,
  EMMATableChangeEvent,
  EMMATableSortChangeEvent,
  CellAllTypes,
} from './emma-table.types';

const equal = (a: unknown, b: unknown) => a === b && JSON.stringify(a) === JSON.stringify(b);
/**
 * Muestra una tabla con paginado síncrono, ordenación por columnas, etc
 */
@Component({
    selector: 'emma-table',
    templateUrl: './emma-table.component.html',
    standalone: false
})
export class EMMATableComponent extends OnUnsubscribe implements OnInit, OnChanges {
  @ViewChild('pSearch', { static: false }) public pSearch?: NgbPopover;
  @ViewChild('tableScrolling', { static: false }) public tableScrolling?: ElementRef<HTMLTableSectionElement>;
  @ViewChild('thead', { static: false }) public thead?: ElementRef<HTMLTableSectionElement>;
  /** Identificador de la vista para guardar en back la selección de columnas */
  @Input() isDebug?: boolean;
  @Input() viewKey?: string;
  /** Título de la tabla */
  @Input() caption?: string;
  /** Columnas disponibles */
  @Input() columns: EMMATableColumn[] = [];
  /** Unidades para los sumatorios */
  @Input() countUnits: string = $localize`resultados`;
  /** Unidades para los sumatorios. Solo una fila. */
  @Input() countUnit: string = $localize`resultado`;
  /** Botón para crear elementos, emite (new) */
  @Input() showCreateButton = false;
  /** Texto botón crear elementos */
  @Input() createButtonText: string | undefined = $localize`Crear`;
  /** Botón de descarga de datos, emite ($download), descarga CSV */
  @Input() showDownloadButton = false;
  /** Texto botón descarga de datos */
  @Input() downloadButtonText: string | undefined = $localize`Descargar datos`;
  /** Nombre de fichero de descarga de datos */
  @Input() downloadFilename?: string;
  /** Botón de exportar datos, emite ($export) */
  @Input() showExportButton = false;
  /** Texto botón de exportar datos */
  @Input() exportButtonText: string | undefined = $localize`Exportar datos`;
  /** Campo/s que usar con el buscador */
  @Input() filterBy?: string | string[];
  _filterBy: string | string[] = '';
  /** Campo con el que agrupar las filas para los sumarios */
  @Input() groupRowsBy: string | number | symbol = '';
  /** Filas por página */
  @Input() limit = 25;
  /** Cambio de filas por página */
  @Output() limitChange = new EventEmitter<number>();
  /** Selector de filas por página */
  @Input() limits = [10, 25, 50, 100];
  /** Muestra que está cargando */
  @Input() loading = false;
  /** Cargando lista de selección de usuario */
  loadingUserMeta = false;
  /** Muestra que hubo errores */
  @Input() error = '';
  /** Columnas mínimas seleccionadas */
  @Input() minVisibleColumns = 1;
  /** Columnas máximas seleccionadas */
  @Input() maxVisibleColumns = 30;
  /** Acciones por fila */
  @Input() rowActions: EMMATableAction[] = [];
  /** Acciones por fila, recibe la fila, devuelve las acciones */
  @Input() rowActionsCallback!: (row: unknown) => EMMATableAction[];
  /** Filas */
  @Input() rows: unknown[] = [];
  /** Mostrar sumario */
  @Input() showSummary = false;
  /** Posición del sumario */
  @Input() summaryPosition: SUMMARY_POSITION = SUMMARY_POSITION.TOP;
  /** Muestra la calculadora del sumario */
  @Input() showCalcSummary = false;
  /** Muestra la barra de búsqueda */
  @Input() showSearch = true;
  /** Callback para personalizar el sumario, recibe un objeto como una fila */
  @Input() summaryPostprocessor?: (summary: any) => any;
  /**  Colores de Select */
  @Input() colorSelect? = 'link-emmablue';
  /**  Colores de Buttons */
  @Input() colorBtns? = 'emmablue';
  /**  Colores de Summary Row Buttons */
  @Input() colorSummaryBtn? = 'inverse-emmadarkblue';
  /** Filtrar por tipo de fila */
  @Input() showTypeItems = false;
  /** Muestra selector de agrupar */
  @Input() showGroupBy = false;
  /** Botones de acciones de tabla */
  @Input() tableActions: EMMATableAction[] = [];
  /** Columnas fijas, no se pueden deseleccionar */
  @Input() fixedColumns: string[] = [];
  /** Muestra el selector de columnas */
  @Input() showSelectColumns = true;
  /** Evita renderizados extra en base a una propiedad identificadora */
  @Input() trackRowsByProp?: string;
  /** Botón crear pulsado */
  @Output() new = new EventEmitter<void>();
  /** Botón descargar pulsado */
  @Output() $download = new EventEmitter<void>();
  /** Botón exportar pulsado */
  @Output() $export = new EventEmitter<void>();
  /** Ha cambiado la tabla, sean las filas o las columnas */
  @Output() $change = new EventEmitter<EMMATableChangeEvent>();
  /** Se ha intentado sobrepasar el límite máximo de columnas seleccionadas */
  @Output() hasExceddedMaxColumns = new EventEmitter<void>();
  /** Todas las filas */
  availableRows: any[] = [];
  /** Filas visibles */
  visibleRows: any[] = [];

  /** Columnas seleccionables */
  selectableColumns: EMMATableColumn[] = [];
  /** Cambia la selección de columnas */
  @Input() selectColumns: string[] = [];
  /** Columnas seleccionadas */
  selectedColumns: string[] = [];
  /** Columnas guardadas en meta */
  storedColumns: string[] = [];
  /** Controla que solo haya una llamada de guardar columnas a la vez */
  $storingColumns = new EventEmitter<void>();

  /** Columnas visibles */
  visibleColumns: EMMATableColumn[] = [];
  /** Columnas por las que se puede agrupar */
  groupableColumns: EMMATableColumn[] = [];
  /**
   * Input del buscador
   *
   * Solo actúa directamente sobre los datos si la paginación es local.
   */
  @Input() inputFilter = '';
  /** lower case input filter */
  @Input() inputFilterLowerCase = true;
  /** Mínima longitud del filtro de entrada */
  @Input() minInputFilterLength = 3;
  /** Activa el botón de buscar */
  @Input() inputFilterButton = false;
  /** Texto del botón de buscar */
  @Input() inputFilterButtonText = '';
  /** Icono del botón de buscar */
  @Input() inputFilterButtonIcon = 'search';
  /** Emite el cambio del input, sea en tiempo real o con clic */
  @Output() inputFilterChange = new EventEmitter<string>();
  /** Fila con el menú de acciones activo */
  activeRowActionsMenu: number | null = null;

  /** Activa el modo de paginación local, que genera páginas con los datos que tiene */
  @Input() hasLocalPagination = true;
  /** Página actual */
  @Input() curPage = 0;
  /** Ha cambiado la página */
  @Output() curPageChange = new EventEmitter<number>();
  /** Última página */
  @Input() maxPage = 0;
  /** Hay, al menos, otra página */
  @Input() hasMorePages = false;

  /** Propiedad para ordenar */
  @Input() sortProp = '';
  @Input() canEditProp = '';
  /** Dirección de ordenación */
  @Input() sortDirection: COLUMN_SORT_DIRECTION = COLUMN_SORT_DIRECTION.ASC;
  /** Cualquier cambio en la ordenación */
  @Output() sortChange = new EventEmitter<EMMATableSortChangeEvent>();

  /** Helper de template para coger propiedades */
  getProp = get;
  /** Caché de la fila sumario */
  _summaryCache: any = {};
  /** Opciones de cálculo de sumario */
  calcSummaryOptions: any[] = [
    {
      label: 'Suma',
      value: CALCULATOR_METHOD.SUM,
    },
    {
      label: 'Media',
      value: CALCULATOR_METHOD.AVERAGE,
    },
  ];
  /** Opción de cálculo seleccionada */
  calcSummary = CALCULATOR_METHOD.SUM;

  // Custom templates
  /** Template de acciones a la izquierda de la cabecera de la tabla (descargar, exportar, etc) */
  @ContentChild(EMMATableActionsLeftTemplateDirective, { read: TemplateRef, static: false })
  tableActionsLeftTemplate!: TemplateRef<any>;
  /** Template de acciones a la derecha de la cabecera de la tabla (buscar, crear, etc) */
  @ContentChild(EMMATableActionsRightTemplateDirective, { read: TemplateRef, static: false })
  tableActionsRightTemplate!: TemplateRef<any>;
  /** Template cuando no hay resultados */
  @ContentChild(EMMATableNoResultsTemplateDirective, { read: TemplateRef, static: false })
  tableNoResultsTemplate!: TemplateRef<any>;
  /**
   * Template del header
   *
   * Directiva: emma-table-header-tmp
   *
   * Contexto: {
   *  rows,
   *  visibleRows,
   *  columns,
   *  visibleColumns,
   *  selectedColumns
   * }
   */
  @ContentChild(EMMATableHeaderTemplateDirective, { read: TemplateRef, static: false })
  tableHeaderTemplate!: TemplateRef<any>;
  /**
   * Template del footer
   *
   * Directiva: emma-table-footer-tmp
   *
   * Contexto: {
   *  rows,
   *  visibleRows,
   *  columns,
   *  visibleColumns,
   *  selectedColumns
   * }
   */
  @ContentChild(EMMATableFooterTemplateDirective, { read: TemplateRef, static: false })
  tableFooterTemplate!: TemplateRef<any>;

  private scrollTimeout: ReturnType<typeof setTimeout> | undefined;
  private isTheadTranslated = false;
  private emmaHeaderHeight = 0;
  private emmaFilterBarHeight = 0;
  private emmaHeaderFilterBarHeight = 0;
  private emmaHeader!: ElementRef<HTMLElement | null>;
  private emmaFilterBar!: ElementRef<HTMLElement | null>;

  constructor(
    private userMetaService: UserMetaService,
    private csvExporterService: CsvExporterService,
    private renderer: Renderer2
  ) {
    super();
  }

  ngOnInit(): void {
    if (!environment.production && !this.viewKey) {
      console.warn('Undefined viewKey, unable to save table columns');
    }
    this.updateTrackRowsByProp();
    this.updateRows();
    if (this.viewKey) {
      this.loadingUserMeta = true;
      this.userMetaService
        .getTableColumns(this.viewKey)
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe((userMetaStoredColumns) => {
          this.loadingUserMeta = false;
          if (userMetaStoredColumns?.length) {
            this.storedColumns = userMetaStoredColumns;
            this.changeColumns(userMetaStoredColumns, false);
          }
        });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('rows' in changes || (this.hasLocalPagination && 'inputFilter' in changes)) {
      this.updateRows();
    }
    if ('limit' in changes) {
      this.onChangeLimit(this.limit);
    }
    if ('curPage' in changes || 'maxPage' in changes || 'hasLocalPagination' in changes) {
      if (this.hasLocalPagination) {
        // Apply local pagination
        this.updateLocalPagination(this.curPage);
      }
    }
    if ('selectColumns' in changes) {
      this.log('ngOnChanges selectColmuns', {
        from: changes.selectColumns.previousValue,
        to: changes.selectColumns.currentValue,
      });
      // Order doesn't matter, compare elements
      if (!haveArraysSameValues(this.selectColumns, this.selectedColumns)) {
        this.changeColumns(this.selectColumns, !changes.selectColumns.firstChange);
      }
    }
    if ('columns' in changes || 'fixedColumns' in changes) {
      this.onColumnsDefinitionChanges();
      this.changeColumns(this.selectedColumns, false);
    }
    if ('filterBy' in changes) {
      this.updateFilterByColumns();
    }
  }

  /** Create new item */
  public onNew(): void {
    this.new.emit();
  }

  /** Export table data: use column names, filter data, clean nulls */
  public onDownload(): void {
    if (this.availableRows?.length) {
      const filename =
        this.downloadFilename || GENERIC_EXPORT_FILENAME + (this.viewKey ? `_${this.viewKey}` : '');
      this.$download.emit();
      this.csvExporterService.exportCsv({
        rows: this.availableRows,
        columns: this.visibleColumns,
        filename,
      });
    }
  }

  /** Custom export */
  public onExport(): void {
    this.$export.emit();
  }

  onChangeColumns = (newSelectedColumns: string[] = []): void => {
    this.log('onChangeColumns', { newSelectedColumns });
    if (newSelectedColumns.length > this.maxVisibleColumns) {
      this.hasExceddedMaxColumns.emit();
    }
    this.changeColumns(newSelectedColumns, true);
  };

  /** Update selected columns when available columns definition changes */
  private onColumnsDefinitionChanges() {
    this.updateTrackRowsByProp();
    this.selectableColumns = this.columns.filter((c) => c.prop && !c.noSelectable);
    if (this.storedColumns.length) {
      if (this.storedColumns.some((sc) => !this.columns.find((c) => sc === c.prop))) {
        // Selected unknown columns
        this.selectedColumns = this.storedColumns.filter((sc) => this.columns.find((c) => sc === c.prop));
      } else {
        this.selectedColumns = [...this.storedColumns];
      }
    }
    if (!this.selectedColumns.length) {
      // First selection or empty
      this.selectedColumns = this.columns
        .filter((c) => !c.hiddenByDefault && !c.noSelectable)
        .map((c) => String(c.prop));
    }
  }

  /** Filter visible columns by select */
  private changeColumns = (newSelectedColumns: string[] = [], shouldStoreUserMeta = true) => {
    let { visibleColumns, groupableColumns } = this;
    let selectedColumns = newSelectedColumns.filter((nsc) => this.columns.find((c) => c.prop === nsc));
    if (selectedColumns.length > this.maxVisibleColumns) {
      selectedColumns = selectedColumns.slice(0, this.maxVisibleColumns);
    }
    const afterFilter = this.columns.filter(
      (c) =>
        c.prop && (selectedColumns.includes(String(c.prop)) || this.fixedColumns.includes(String(c.prop)))
    );
    if (afterFilter.length >= this.minVisibleColumns) {
      visibleColumns = afterFilter;
    } else {
      selectedColumns = visibleColumns.filter((c) => c.prop && !c.noSelectable).map((c) => String(c.prop));
    }
    // Order does not matter
    // selectedColumns que son las que acaban siendo visibles tiene que ser consistente
    // con la acción del usuario (newSelectedColumns)
    if (!haveArraysSameValues(this.selectedColumns, selectedColumns, newSelectedColumns)) {
      this.selectedColumns = selectedColumns;
    }
    // If grouped, group column goes first
    if (this.groupRowsBy) {
      const idxGroup = visibleColumns.findIndex((col) => col.prop === this.groupRowsBy);
      if (idxGroup >= 0) {
        visibleColumns.unshift(visibleColumns.splice(idxGroup, 1)[0]);
      }
    }
    if (this.showGroupBy) {
      groupableColumns = visibleColumns.filter((col) => !col.dataType || col.dataType === 'default');
    }
    // Order does not matter
    if (!haveArraysSameValues(this.groupableColumns, groupableColumns)) {
      this.groupableColumns = groupableColumns;
    }
    // Order matters
    if (!areArraysEquivalent(this.visibleColumns, visibleColumns)) {
      this.visibleColumns = visibleColumns;
      if (this.showSummary) {
        this.updateSummaryCache();
      }
      this.emitChange('columns');
    }
    // Local filtering is subject to change
    this.updateFilterByColumns();
    if (this.inputFilter && this.hasLocalPagination) {
      this.updateRows();
    }
    this.log('changeColumns', {
      newSelectedColumns,
      storedColumns: this.storedColumns,
      visibleColumns: this.visibleColumns,
      selectedColumns: this.selectColumns,
    });
    if (shouldStoreUserMeta) {
      this._storeColumns();
    }
  };

  /** Filter and alter visible rows */
  private updateRows = () => {
    let availableRows: Array<any>;
    const { hasLocalPagination, _filterBy, inputFilter, minInputFilterLength, rows } = this;
    if (hasLocalPagination) {
      if (inputFilter?.length >= minInputFilterLength && _filterBy.length) {
        const filter = filterRowFactory(_filterBy, inputFilter);
        availableRows = rows.filter(filter);
      } else {
        availableRows = [...rows];
      }
      // Sort rows
      if (this.sortProp) {
        const comparator = this.columns.find((column) => column.prop === this.sortProp)?.sortComparator;
        availableRows.sort(
          comparator
            ? (rowA, rowB) => comparator(rowA, rowB) * this.sortDirection
            : sortByProp(this.sortProp, this.sortDirection)
        );
      }
      // Add group rowns
      if (this.groupRowsBy) {
        availableRows = this.getGroupRows(availableRows);
      }
      // Whenever the filter changes, always go back to the first page
      if (!equal(this.availableRows, availableRows)) {
        this.availableRows = availableRows;
        if (this.showSummary) {
          this.updateSummaryCache();
        }
        this.emitChange('rows');
        this.onChangePage(0);
      }
    } else if (!equal(this.availableRows, this.rows)) {
      this.availableRows = rows;
      this.visibleRows = rows;
      if (this.showSummary) {
        this.updateSummaryCache();
      }
    }
    this.log('updateRows', { availableRows: this.availableRows, visibleRows: this.visibleRows });
  };

  /** Store selected columns into the user-meta API service */
  private _storeColumns() {
    if (this.viewKey) {
      this.$storingColumns.emit();
      const selectedColumns = this.selectedColumns.slice(0);
      this.storedColumns = selectedColumns;
      this.userMetaService
        .setTableColumns(this.viewKey, selectedColumns)
        .pipe(takeUntil(this.$storingColumns))
        .subscribe((response) => {
          this.log('userMetaService: Stored columns', {
            storedColumns: selectedColumns,
            responseStatus: response.status,
          });
        });
    }
  }

  /**
   * Process rows to add a summary of values as a group row.
   * Auxiliary function for `getGroupRows`.
   */
  private _processGroupRow({ groupRowsBy, currentGroup, currentGroupName, groupedRows }: any) {
    const groupRow = this.visibleColumns.reduce((acc: any, col: EMMATableColumn) => {
      let val: any = '';
      if (col.prop === groupRowsBy) {
        val = currentGroupName;
      } else {
        const values = currentGroup.map((g: any) => get(g, col.prop));
        val = NUMBER_TYPES.includes(col.dataType as any)
          ? getColumnValue(values, col, this.calcSummary)
          : getColumnSummary(values, col, this.calcSummary);
      }
      return set(acc, col.prop, val);
    }, {});
    return [...groupedRows, { ...groupRow, __groupRow: true }, ...currentGroup];
  }

  /**
   * Insert group rows
   */
  private getGroupRows = (rows: any[]) => {
    const groupRowsBy = this.groupRowsBy || '';
    rows.sort(
      sortByProp(groupRowsBy, this.sortProp === groupRowsBy ? this.sortDirection : COLUMN_SORT_DIRECTION.ASC)
    );
    let groupedRows: any[] = [];
    let currentGroup: any[] = [];
    let currentGroupName: any = get(rows[0], groupRowsBy);
    rows.forEach((row, i, arr) => {
      if (currentGroupName === get(row, groupRowsBy)) {
        currentGroup.push(row);
      } else {
        groupedRows = this._processGroupRow({
          groupRowsBy,
          currentGroup,
          currentGroupName,
          groupedRows,
        });
        currentGroup = [row];
        currentGroupName = get(row, groupRowsBy);
      }
    });
    // Save the last group
    if (currentGroup.length) {
      groupedRows = this._processGroupRow({
        groupRowsBy,
        currentGroup,
        currentGroupName,
        groupedRows,
      });
    }
    return groupedRows;
  };

  /**
   * Cache summaries to avoid re-render.
   */
  private updateSummaryCache = () => {
    for (const col of this.visibleColumns) {
      // tslint:disable-next-line: strict-type-predicates
      if (col.prop !== undefined) {
        const values = chain(this.availableRows)
          .filter(Boolean)
          .filter((row) => !row.__groupRow)
          .map((row) => get(row, col.prop))
          .value();
        set(this._summaryCache, col.prop, getColumnSummary(values, col, this.calcSummary));
      }
    }
    if (this.summaryPostprocessor) {
      this._summaryCache = this.summaryPostprocessor(this._summaryCache);
    }
  };

  /**
   * Render multi-select text.
   */
  public getMultiLabel = (values: any[]): string => {
    const totalColumns = this.selectableColumns.filter(Boolean).length;
    return $localize`Mostrando ${formatNumber(values.length)} de ${formatNumber(totalColumns)} columnas`;
  };

  /**
   * Show actions menu
   */
  public onRowActionsMenu(active: boolean, rowId = -1): void {
    if (active) {
      this.activeRowActionsMenu = rowId;
      return;
    }

    if (this.activeRowActionsMenu === rowId || rowId === -1) {
      this.activeRowActionsMenu = null;
    }
  }

  /**
   * Emit action on table
   */
  // eslint-disable-next-line class-methods-use-this
  public onActionClick(event: Event, action: EMMATableAction): void {
    if (action.$click) {
      action.$click.emit();
    }
  }

  /**
   * Emit that table has changed and how
   */
  public emitChange(type: 'columns' | 'rows'): void {
    this.$change.emit({
      type,
      rows: this.availableRows,
      columns: this.visibleColumns,
      selectedColumns: this.selectedColumns,
    });
  }

  /**
   * Page has changed
   */
  public onChangePage = (pageNumber = 0): void => {
    // Local pagination, we have every needed row
    if (this.hasLocalPagination) {
      this.updateLocalPagination(pageNumber);
    } else {
      this.curPage = pageNumber;
    }
    this.curPageChange.emit(pageNumber);
  };

  /**
   * Update local pagination
   */
  private updateLocalPagination = (pageNumber: number) => {
    const pages = Math.ceil(this.availableRows.length / this.limit);
    this.maxPage = Math.max(pages - 1, 0);
    const offset = this.limit * pageNumber;
    this.curPage = pageNumber;
    this.visibleRows = this.availableRows.slice(offset, offset + this.limit);
  };

  /**
   * Limit has changed
   */
  public onChangeLimit = (limit: number): void => {
    this.limit = limit;
    this.limitChange.emit(limit);
    this.onChangePage(0);
  };

  /**
   * Filter visible rows by input
   */
  public onChangeFilter = (value: string): void => {
    this.inputFilter = this.inputFilterLowerCase ? value.toLowerCase() : value;
    if (this.pSearch) {
      if (this.inputFilter && this.inputFilter.length < this.minInputFilterLength) {
        if (!this.pSearch.isOpen()) {
          this.pSearch.open();
        }
      } else if (this.pSearch.isOpen()) {
        this.pSearch.close();
      }
    }
    this._applyInputFilterDebounce();
  };

  /**
   * Trigger input filter, either by intro or button
   */
  public applyInputFilter = (): void => {
    if (this.inputFilter.length === 0 || this.inputFilter.length >= this.minInputFilterLength) {
      this.inputFilterChange.emit(this.inputFilter);
      this.updateRows();
    }
  };

  // tslint:disable-next-line: member-ordering
  private _applyInputFilterDebounce = debounce(this.applyInputFilter, INPUT_FILTER_DEBOUNCE_DELAY);

  /**
   * Group rows by prop
   */
  public onChangeGroupBy = (groupRowsBy: string | number | symbol): void => {
    this.groupRowsBy = groupRowsBy;
    this.changeColumns(this.selectedColumns);
    this.updateRows();
  };

  /**
   * Sort has changed
   * Cycle: ASC > DESC > NONE
   */
  public onChangeSort = (prop: string): void => {
    if (!prop) {
      this.sortProp = prop;
      this.sortDirection = COLUMN_SORT_DIRECTION.NONE;
    } else if (this.sortProp !== prop) {
      this.sortProp = prop;
      this.sortDirection = COLUMN_SORT_DIRECTION.ASC;
    } else {
      if (this.sortDirection === COLUMN_SORT_DIRECTION.NONE) {
        this.sortDirection = COLUMN_SORT_DIRECTION.ASC;
      } else if (this.sortDirection === COLUMN_SORT_DIRECTION.ASC) {
        this.sortDirection = COLUMN_SORT_DIRECTION.DESC;
      } else {
        this.sortDirection = COLUMN_SORT_DIRECTION.NONE;
      }
    }
    this.sortChange.emit({ direction: this.sortDirection, prop: this.sortProp });
    this.updateRows();
    this.log('onChangeSort', { direction: this.sortDirection, prop: this.sortProp });
  };

  /**
   * Summary calculator has changed
   */
  public onChangeCalcSummary = (calc: CALCULATOR_METHOD): void => {
    this.calcSummary = calc;
    this.updateSummaryCache();
    this.updateRows();
  };

  /**
   * Track rows by property
   */
  public trackByRowId(index: number, row: unknown): unknown | undefined {
    if (this.trackRowsByProp) {
      return get(row, this.trackRowsByProp);
    }
    return undefined;
  }

  /**
   * Track columns by 'prop' property
   */
  public trackByColumnProp = (index: number, column: EMMATableColumn): string => column.prop;

  /**
   * Assign complex default values
   */
  public updateTrackRowsByProp(): void {
    // Initial value to track rows by ID when available
    if (this.trackRowsByProp === undefined) {
      if (this.columns.some((col) => col.prop === 'id')) {
        this.trackRowsByProp = 'id';
      } else {
        this.log('No column with prop "id" found, trackRowsByProp will be undefined');
      }
    }
  }

  private updateFilterByColumns(): void {
    if (this.filterBy !== undefined) {
      this._filterBy = this.filterBy;
    } else {
      this._filterBy = this.visibleColumns
        .filter(
          (col) =>
            !col.dataType ||
            [
              COLUMN_DATA_TYPE.STRING,
              COLUMN_DATA_TYPE.URL,
              COLUMN_DATA_TYPE.DEFAULT,
              COLUMN_DATA_TYPE.CLIPBOARD,
              COLUMN_DATA_TYPE.LIST,
              COLUMN_DATA_TYPE.ICON_LIST,
              COLUMN_DATA_TYPE.LIST_LENGTH,
              COLUMN_DATA_TYPE.ACTION,
              COLUMN_DATA_TYPE.ACTION_ICON_LIST,
            ].includes(col.dataType)
        )
        .map((col) => col.prop);
    }
  }

  private log = (...params: unknown[]): void => {
    if (this.isDebug) {
      const prefix = '[table' + (this.viewKey ? ` ${this.viewKey}` : '') + ']';
      console.log(prefix, ...params);
    }
  };

  onRowCellActionEvent(col: EMMATableColumn, row: any, value?: CellAllTypes) {
    if (!isNil(value)) {
      col.$rowCellAction?.emit({ ...row, [col.prop]: value });
    } else {
      col.$rowCellAction?.emit(row);
    }
  }

  /** Update positions header of table */
  @HostListener('window:scroll', ['$event'])
  onScroll(): void {
    if (this.thead) {
      if (!this.emmaFilterBar) {
        this.emmaFilterBar = new ElementRef(document.querySelector('emma-filter-bar'));
      }

      if (!this.emmaHeader) {
        this.emmaHeader = new ElementRef(document.querySelector('header'));
      }

      if (this.isTheadTranslated) {
        this.renderer.setStyle(this.thead.nativeElement, 'transform', 'translate3d(0, 0, 0)');
        this.isTheadTranslated = false;
      }

      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = setTimeout(() => {
        requestAnimationFrame(() => {
          const boundingClient = this.tableScrolling?.nativeElement?.getBoundingClientRect();
          if (boundingClient) {
            this.updateTableHeaderPosition(boundingClient);
          }
        });
      }, 350);
    }
  }

  private updateDimensions(): void {
    this.emmaHeaderHeight = Number(this.emmaHeader.nativeElement?.offsetHeight);
    this.emmaFilterBarHeight = Number(this.emmaFilterBar.nativeElement?.offsetHeight);
    this.emmaHeaderFilterBarHeight = this.emmaHeaderHeight + this.emmaFilterBarHeight;
  }

  private updateTableHeaderPosition(pxtop: DOMRect): void {
    if (this.thead && this.emmaHeader && this.emmaFilterBar) {
      this.updateDimensions();

      let newTopPosition = 0;
      if (pxtop.top < 0) {
        newTopPosition = Math.abs(pxtop.top) + this.emmaHeaderFilterBarHeight;
        this.isTheadTranslated = true;
      } else if (pxtop.top < this.emmaHeaderFilterBarHeight) {
        newTopPosition = this.emmaHeaderFilterBarHeight - pxtop.top;
        this.isTheadTranslated = true;
      }

      if (this.isTheadTranslated) {
        this.renderer.setStyle(
          this.thead.nativeElement,
          'transform',
          `translate3d(0, ${newTopPosition}px, 0)`
        );
      }
    }
  }
}
